import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, FileText, CalendarRange, Banknote, Check, Loader2 } from "lucide-react";

const STAGES = ["Brouillon", "Envoyé", "Signé"];
const toLocal = (iso) => (iso ? iso.slice(0, 10) : "");

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-0.5 w-full h-8 rounded-lg border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
    </label>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="pt-2 first:pt-0">
      <p className="text-[11px] font-bold uppercase tracking-wide text-foreground flex items-center gap-1 mb-1"><Icon className="w-3 h-3 text-emerald-600" /> {title}</p>
      <div className="grid grid-cols-2 gap-1.5">{children}</div>
    </div>
  );
}

export default function ProjectDraftReview({ draft: initial, onDone }) {
  const [d, setD] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (sec, key) => (v) => setD((p) => ({ ...p, [sec]: { ...p[sec], [key]: v } }));
  const setDate = (key) => (v) => setD((p) => ({ ...p, schedule: { ...p.schedule, [key]: v ? `${v}T${key === "endAt" ? "18" : "08"}:00:00` : null } }));
  const num = (v) => (v === "" ? null : parseFloat(v));
  const total = d.quote.amount || 0;
  const dep = d.finance.depositAmount || 0;

  const commit = async () => {
    setSaving(true);
    try {
      await api.post("/projects/commit", d);
      setSaved(true);
      toast.success(d.quote.stage === "Signé" ? "Projet planifié et ajouté au prévisionnel ✅" : "Client et devis enregistrés ✅");
      window.dispatchEvent(new CustomEvent("birdie:changed"));
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  if (saved) return <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2.5 text-xs font-semibold text-emerald-700 flex items-center gap-1.5" data-testid="project-draft-saved"><Check className="w-4 h-4" /> Enregistré : client, devis{d.quote.stage === "Signé" ? ", planning et échéancier" : ""}.</div>;

  return (
    <div className="mt-2 rounded-xl border border-emerald-500/30 bg-background p-2.5 space-y-1 divide-y divide-border/60" data-testid="project-draft-card">
      <Section icon={User} title="Client">
        <Field label="Prénom" value={d.client.firstName} onChange={set("client", "firstName")} testid="draft-client-firstname" />
        <Field label="Nom" value={d.client.lastName} onChange={set("client", "lastName")} testid="draft-client-lastname" />
        <Field label="Téléphone" value={d.client.phone} onChange={set("client", "phone")} testid="draft-client-phone" />
        <Field label="Adresse" value={d.client.address} onChange={set("client", "address")} testid="draft-client-address" />
      </Section>
      <Section icon={FileText} title="Devis">
        <div className="col-span-2"><Field label="Prestation" value={d.quote.title} onChange={set("quote", "title")} testid="draft-quote-title" /></div>
        <Field label="Montant total (€)" type="number" value={d.quote.amount} onChange={(v) => set("quote", "amount")(num(v))} testid="draft-quote-amount" />
        <div>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Statut</span>
          <div className="mt-0.5 flex gap-1">{STAGES.map((s) => (
            <button key={s} onClick={() => set("quote", "stage")(s)} data-testid={`draft-stage-${s}`}
              className={`flex-1 h-8 rounded-lg text-[10px] font-bold border transition-colors ${d.quote.stage === s ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground"}`}>{s}</button>))}
          </div>
        </div>
        {d.quote.lines?.length > 0 && <ul className="col-span-2 text-[11px] text-muted-foreground">{d.quote.lines.map((l, i) => <li key={i} className="flex justify-between"><span>• {l.label}</span>{l.amount ? <span className="tabular-nums">{l.amount.toLocaleString("fr-FR")} €</span> : null}</li>)}</ul>}
      </Section>
      <Section icon={CalendarRange} title="Planning">
        <Field label="Début" type="date" value={toLocal(d.schedule.startAt)} onChange={setDate("startAt")} testid="draft-start" />
        <Field label="Fin" type="date" value={toLocal(d.schedule.endAt)} onChange={setDate("endAt")} testid="draft-end" />
      </Section>
      <Section icon={Banknote} title="Échéancier">
        <Field label="Acompte (€)" type="number" value={d.finance.depositAmount} onChange={(v) => set("finance", "depositAmount")(num(v))} testid="draft-deposit" />
        <Field label="Solde prévu le" type="date" value={d.finance.expectedPaymentDate} onChange={set("finance", "expectedPaymentDate")} testid="draft-expected" />
        {total > 0 && <p className="col-span-2 text-[11px] text-muted-foreground" data-testid="draft-breakdown">Acompte <b className="text-emerald-700">{dep.toLocaleString("fr-FR")} €</b> · Solde <b className="text-foreground">{Math.max(total - dep, 0).toLocaleString("fr-FR")} €</b></p>}
      </Section>
      <div className="pt-2">
        <button onClick={commit} disabled={saving} data-testid="draft-commit-btn"
          className="w-full h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider et tout créer
        </button>
      </div>
    </div>
  );
}
